'use client';

import classNames from 'classnames';
import Link from 'next/link';
import type { ReactNode } from 'react';

import { EVENT_NAMES, trackEvent } from '@/analytics';
import { ExternalLink } from '@/components/ExternalLink';
import { Logo } from '@/components/Logo';
import { RecaptchaTerms } from '@/components/RecaptchaTerms';
import { CornerCropMark } from '@/components/SiteFooter/CornerCropMark';
import { Typography } from '@/components/Typography';
import { Wrapper } from '@/components/Wrapper';
import { SITE_BUSINESS_NAME } from '@/constants';
import {
  ABOUT_PAGE_PATH,
  CAREERS_PAGE_PATH,
  CONTACT_PAGE_PATH,
  COOKIE_POLICY_PATH,
  PARTNER_WITH_US_PATH,
  PRESS_PATH,
  PRIVACY_POLICY_PATH,
  SOCIAL_LINKS,
  TERMS_OF_SERVICE_PATH,
  topicPath,
} from '@/helpers/linkHelpers';
import ArrowSquareOut from '@/icons/react/ArrowSquareOut';
import FacebookLogo from '@/icons/react/FacebookLogo';
import InstagramLogo from '@/icons/react/InstagramLogo';
import ThreadsLogo from '@/icons/react/ThreadsLogo';
import TikTokLogo from '@/icons/react/TikTokLogo';
import XLogo from '@/icons/react/XLogo';
import YoutubeLogo from '@/icons/react/YoutubeLogo';
import type { SiteNavMenuType } from '@/lib/firestore/dataFetchers/getSiteNavMenu';

export interface SiteFooterProps {
  menuItems?: SiteNavMenuType;
}

type FooterLinkType = {
  label: ReactNode;
  href: string;
  external?: boolean;
};

function LinkBlock({
  title,
  links,
}: {
  title: ReactNode;
  links: FooterLinkType[];
}) {
  const classes = 'group-link flex gap-2 items-center text-paper py-1';

  function handleFooterLinkClick(e: React.MouseEvent<HTMLAnchorElement>) {
    trackEvent(EVENT_NAMES.footer_click, {
      link_text: e.currentTarget.innerText,
      link_label: e.currentTarget.innerText,
      link_url: e.currentTarget.href,
      context: 'site_footer_link',
    });
  }

  return (
    <div className="flex flex-col gap-1 md:gap-3 basis-full sm:basis-1/2 md:basis-1/4">
      <Typography name="tag-350" tag="h4" className="text-gray-300">
        {title}
      </Typography>
      <ul className="flex flex-col gap-1">
        {links.map((link) =>
          link.external ? (
            <ExternalLink
              key={link.href}
              href={link.href}
              trackingContext="site_footer_link"
              className={classes}
            >
              {link.label}
              <ArrowSquareOut className="text-gray-500 translate-y-px" />
            </ExternalLink>
          ) : (
            <Link
              key={link.href}
              href={link.href}
              className={classes}
              onClick={handleFooterLinkClick}
            >
              {link.label}
            </Link>
          ),
        )}
      </ul>
    </div>
  );
}

export function SiteFooter({ menuItems }: SiteFooterProps) {
  const currentYear = new Date().getFullYear();

  const topicLinks =
    menuItems?.map((item) => ({
      href: topicPath(item.category?.slug),
      label: item.category?.title,
      external: false,
    })) || [];

  const companyLinks = [
    {
      label: 'About 1440',
      href: ABOUT_PAGE_PATH,
    },
    {
      label: 'Press',
      href: PRESS_PATH,
    },
    {
      label: 'Partner With Us',
      href: PARTNER_WITH_US_PATH,
    },
    {
      label: 'Contact Us',
      href: CONTACT_PAGE_PATH,
    },
    {
      label: 'Careers',
      href: CAREERS_PAGE_PATH,
    },
  ];

  const followUsLinks = [
    {
      label: (
        <>
          <FacebookLogo className="translate-y-px" /> Facebook
        </>
      ),
      href: SOCIAL_LINKS.facebook.url,
      external: true,
    },
    {
      label: (
        <>
          <InstagramLogo className="translate-y-px" /> Instagram
        </>
      ),
      href: SOCIAL_LINKS.instagram.url,
      external: true,
    },
    {
      label: (
        <>
          <ThreadsLogo className="translate-y-px" /> Threads
        </>
      ),
      href: SOCIAL_LINKS.threads.url,
      external: true,
    },
    {
      label: (
        <>
          <TikTokLogo className="translate-y-px" /> TikTok
        </>
      ),
      href: SOCIAL_LINKS.tiktok.url,
      external: true,
    },
    {
      label: (
        <>
          <XLogo className="translate-y-px" /> Twitter
        </>
      ),
      href: SOCIAL_LINKS.twitter.url,
      external: true,
    },
    {
      label: (
        <>
          <YoutubeLogo />
          YouTube
        </>
      ),
      href: SOCIAL_LINKS.youtube.url,
      external: true,
    },
  ];

  const termsLinks = [
    {
      label: 'Terms & Conditions',
      href: TERMS_OF_SERVICE_PATH,
    },
    {
      label: 'Privacy Policy',
      href: PRIVACY_POLICY_PATH,
    },
    {
      label: 'Cookies',
      href: COOKIE_POLICY_PATH,
    },
  ];

  const swatches = [
    'bg-cyan-400',
    'bg-magenta-400',
    'bg-yellow-500',
    'bg-red-400',
    'bg-green-400',
    'bg-blue-400',
    'bg-orange-400',
    'bg-purple-400',

    'bg-black',
    'bg-gray-600',
    'bg-gray-500',
    'bg-gray-400',
    'bg-gray-300',
    'bg-gray-200',
    'bg-gray-100',
    'bg-paper',
  ];

  return (
    <footer className="flex flex-col">
      {/* Top main footer area */}
      <div className="bg-black py-12">
        <Wrapper>
          <div className="flex flex-col gap-10">
            <div className="flex items-center gap-5">
              <Link
                className="bg-paper flex p-3 hover:bg-gray-200 transition-colors"
                href="/"
              >
                <Logo className="fill-black h-6 w-auto block" />
              </Link>
              <Typography name="body-400" tag="p" className="text-gray-300">
                Fact-driven knowledge.
                <br />
                Curated and explained for the intellectually curious.
              </Typography>
            </div>
            <nav className="flex flex-wrap gap-y-8 justify-between">
              <LinkBlock title="Topic Areas" links={topicLinks} />
              <LinkBlock title="Company" links={companyLinks} />
              <LinkBlock title="Follow 1440" links={followUsLinks} />
              <LinkBlock title="Terms & Policies" links={termsLinks} />
            </nav>
          </div>
        </Wrapper>
      </div>

      {/* Bottom sub-footer area */}
      <div className="bg-gray-700 py-8 overflow-hidden">
        <Wrapper>
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between lg:min-h-5">
            <div className="flex flex-col gap-2 lg:flex-row lg:gap-8 lg:items-center">
              <Typography name="body-300" tag="p" className="text-gray-300">
                © {currentYear} - {SITE_BUSINESS_NAME}
              </Typography>
              <RecaptchaTerms
                classes={{
                  wrapper: 'text-body-300 text-gray-300',
                  links:
                    'text-gray-300 underline hover:text-paper hover:underline-offset-2',
                }}
                trackingContext="site_footer_link"
              />
            </div>
            <div className="flex gap-half flex-wrap max-w-[7.8rem]">
              {swatches.map((swatch) => (
                <div
                  key={swatch}
                  className={classNames('block w-2 h-2 rounded-full', swatch)}
                />
              ))}
            </div>
            <CornerCropMark
              side="left"
              alignmentClassNames="absolute bottom-0 -left-16"
            />
            <CornerCropMark
              side="right"
              alignmentClassNames="absolute bottom-0 -right-16"
            />
          </div>
        </Wrapper>
      </div>
    </footer>
  );
}
